import { useRef, useEffect, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import useWindowSize from 'hooks'
import Tooltip from 'components/Tooltip'
import { createRoot } from 'react-dom/client'

import 'mapbox-gl/dist/mapbox-gl.css'
import './Map.scss'

mapboxgl.accessToken =
  'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'

const Map = ({
  preselectedZoom,
  panTo,
}: {
  preselectedZoom?: number
  panTo: number[] | null
}) => {
  const mapContainerRef = useRef<null | HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map>()
  const tooltipRef = useRef(new mapboxgl.Popup({ offset: 15 }))

  const baseZoom = preselectedZoom ?? 10.5 // MicroFrontend property
  // 46.204016384423724, 6.147693091779846
  const baseLat = 46.204 //46.056
  const baseLng = 6.1477 //6.2675
  const [lng, setLng] = useState(baseLng)
  const [lat, setLat] = useState(baseLat)
  const [zoom, setZoom] = useState(baseZoom)
  const [height, setHeight] = useState(400)
  const size = useWindowSize()

  useEffect(() => {
    if (size.width && size.height) {
      const ratio = size.width / size.height
      if (ratio > 1) {
        // desktop or landscape view
        setZoom(11)
        setHeight(600)
      } else {
        // mobile or portrait view (default)
        setHeight(400)
        setZoom(baseZoom)
        setLat(baseLat)
      }
    }
  }, [size, baseZoom])

  useEffect(() => {
    if (mapRef.current && panTo !== null) {
      const target = new mapboxgl.LngLat(panTo[1], panTo[0])
      // mapRef.current.panTo(target)

      if (mapContainerRef && mapContainerRef.current) {
        mapContainerRef.current.scrollIntoView({ behavior: 'smooth' })
      }
      mapRef.current.easeTo({
        center: target,
        zoom: 12,
        duration: 800,
      })
      // remove previous circle if exists
      const previous_circles = document.getElementsByClassName('pointer-circle')
      while (previous_circles.length > 0) {
        previous_circles[0].parentNode?.removeChild(previous_circles[0])
      }
      // add a circle to point out the place
      const el = document.createElement('div')
      el.className = 'pointer-circle'
      el.style.backgroundSize = '100%'
      new mapboxgl.Marker(el).setLngLat(target).addTo(mapRef.current)
    }
  }, [panTo])
  // Initialize map when component mounts
  useEffect(() => {
    if (!mapContainerRef.current) {
      return
    }
    const bounds = new mapboxgl.LngLatBounds(
      [5.199587, 45.352255], // SW
      [7.569433, 46.844506] // NE
    )
    const map = new mapboxgl.Map({
      container:
        mapContainerRef.current === null ? '' : mapContainerRef.current,
      style: 'mapbox://styles/ta-interaktiv/cl3jwibai002n14p8yxyam55n',
      center: [lng, lat],
      zoom: zoom,
      minZoom: 8,
      maxZoom: 13,
      maxBounds: bounds,
    })
    mapRef.current = map

    // Pointer when user hovers a circle
    map.on('mouseenter', 'festivals_circles', (e) => {
      if (e.features?.length) {
        map.getCanvas().style.cursor = 'pointer'
      }
    })

    // Reset cursor to default
    map.on('mouseleave', 'festivals_circles', () => {
      map.getCanvas().style.cursor = ''
    })

    // Add tooltip when users clicks a circle
    map.on('click', 'festivals_circles', (e) => {
      const features = map.queryRenderedFeatures(e.point)
      if (features.length) {
        const feature = features[0]
        const tooltipNode = document.createElement('div')
        const root = createRoot(tooltipNode)
        root.render(<Tooltip feature={feature} />)

        tooltipRef.current
          .setLngLat(e.lngLat)
          .setDOMContent(tooltipNode)
          .addTo(map)
      }
    })

    map.addControl(new mapboxgl.NavigationControl())
    setTimeout(() => {
      map.addControl(new mapboxgl.GeolocateControl())
    }, 500)

    map.scrollZoom.disable()

    return () => map.remove()
  }, [zoom]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className='map-legend-container'>
        <div className='map-legend'>
          <div className='map-background'>
            <span className='circle rock'></span>
            <span className='label'>Rock/pop/…</span>
            <span className='circle classical'></span>
            <span className='label'>Classique</span>
            <span className='circle jazz'></span>
            <span className='label'>Jazz</span>
            <span className='circle other'></span>
            <span className='label'>Tous styles</span>
            {/* <span className='empty circle big'></span>
            <span className='label'>10'000 festivaliers ou plus</span>
            <span className='empty circle medium'></span>
            <span className='label'>1000-10'000 festivaliers</span>
            <span className='empty circle small'></span>
            <span className='label'>Moins de 1000</span> */}
          </div>
        </div>
      </div>
      <div
        className='map-container'
        style={{ height: height }}
        ref={mapContainerRef}
      />
    </div>
  )
}

export default Map
